<!-- ── Custom panel mode (arbitrary projected content via [ktSelectPanel]) ── -->
<div *ngIf="customPanel" class="pkt-form-field" [class.pkt-error-state]="!!errorMessage"
  [class.pkt-disabled]="disabled" [ngClass]="'pkt-label-' + labelPosition">
  <label *ngIf="label" class="pkt-label" [class.pkt-label--clamp]="labelLines" [style.--pkt-label-lines]="labelLines">
    {{ label }}<span *ngIf="required" class="pkt-required">*</span>
  </label>

  <div #triggerRef class="pkt-vs-trigger" (click)="toggleDropdown()" [class.open]="isOpen" [class.disabled]="disabled"
    cdkOverlayOrigin #origin="cdkOverlayOrigin">
    <span *ngIf="!triggerText" class="pkt-vs-placeholder">{{ placeholder }}</span>
    <span *ngIf="triggerText" class="pkt-vs-value"
      style="flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">{{ triggerText }}</span>
    <div class="pkt-vs-actions">
      <span class="material-symbols-outlined pkt-chevron" [class.rotated]="isOpen">keyboard_arrow_down</span>
    </div>
  </div>

  <ng-template cdkConnectedOverlay [cdkConnectedOverlayOrigin]="origin" [cdkConnectedOverlayOpen]="isOpen"
    [cdkConnectedOverlayWidth]="customPanel ? '' : triggerWidth" [cdkConnectedOverlayHasBackdrop]="true"
    cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop" (backdropClick)="closeDropdown()"
    (detach)="closeDropdown()">
    <div class="pkt-vs-panel" [ngClass]="panelClass">
      <ng-container *ngTemplateOutlet="customPanelDir?.templateRef"></ng-container>
    </div>
  </ng-template>

  <div *ngIf="hint && !errorMessage" class="pkt-hint">{{ hint }}</div>
  <div *ngIf="errorMessage && errorDisplay === 'inline'" class="pkt-error-msg">
    <span class="material-symbols-outlined">error</span>{{ errorMessage }}
  </div>
  <div *ngIf="errorMessage && errorDisplay === 'floating'" class="pkt-error-floating" role="alert">
    <span class="material-symbols-outlined">error</span>{{ errorMessage }}
  </div>
</div>

<!-- ── Standard mat-select (no mat-form-field wrapper) ── -->
<div *ngIf="!virtualScroll && !customPanel" class="pkt-form-field" [class.pkt-error-state]="!!errorMessage"
  [class.pkt-disabled]="disabled" [ngClass]="'pkt-label-' + labelPosition">
  <label *ngIf="label" class="pkt-label" [class.pkt-label--clamp]="labelLines" [style.--pkt-label-lines]="labelLines">
    {{ label }}<span *ngIf="required" class="pkt-required">*</span>
  </label>

  <mat-select #matSelectRef class="pkt-select-input" [ngModel]="value" (ngModelChange)="onMatValueChange($event)"
    (openedChange)="onMatPanelToggle($event)" [multiple]="multiple" [disabled]="disabled" [placeholder]="placeholder"
    [disableOptionCentering]="true" [class.pkt-select-error]="!!errorMessage"
    [panelClass]="['pkt-select-panel', panelClass]"
    [matTooltip]="tooltip" [matTooltipDisabled]="!tooltip" matTooltipPosition="above">
    <!-- With custom option content, force the trigger to show plain label(s), not the projected markup -->
    <mat-select-trigger *ngIf="optionContent">{{ multiple ? selectedItemsLabels : selectedLabel }}</mat-select-trigger>
    <mat-option *ngIf="searchable" class="pkt-search-option" [disabled]="true">
      <ngx-mat-select-search [(ngModel)]="searchQuery" (ngModelChange)="onSearch($event)"
        [disableScrollToActiveOnOptionsChanged]="true"
        [placeholderLabel]="searchPlaceholder" [noEntriesFoundLabel]="noResultsText"></ngx-mat-select-search>
    </mat-option>
    <ng-container *ngIf="!loading">
      <mat-option *ngIf="clearable && !multiple" [value]="null">— None —</mat-option>
      <div *ngIf="multiple && showSelectAll && (!hasGroups || showSelectAllAcrossGroups) && filteredOptions.length > 0" class="pkt-select-all-row">
        <mat-checkbox [checked]="areAllSelected(filteredOptions)" [indeterminate]="areSomeSelected(filteredOptions)"
          (click)="$event.stopPropagation()" (change)="toggleAll(filteredOptions, $event.checked)">
          {{ selectAllLabel }}
        </mat-checkbox>
      </div>
      <ng-container *ngIf="!hasGroups">
        <mat-option *ngFor="let opt of filteredOptions" [value]="opt.value" [disabled]="opt.disabled" [matTooltip]="opt.tooltip || ''" [matTooltipClass]="'multiline-tooltip'" matTooltipPosition="above">
          <ng-container *ngIf="optionContent; else defaultOptLabel"
            [ngTemplateOutlet]="optionContent" [ngTemplateOutletContext]="{ $implicit: opt }"></ng-container>
          <ng-template #defaultOptLabel>{{ opt.label }}</ng-template>
        </mat-option>
      </ng-container>
      <ng-container *ngIf="hasGroups">
        <ng-container *ngFor="let grp of groupedFilteredOptions">
          <ng-container *ngIf="grp.name">
            <!-- Grouped + select-all: the group header is a collapse chevron + toggle checkbox -->
            <mat-optgroup *ngIf="multiple && showSelectAll" class="pkt-optgroup-selectall">
              <mat-icon class="pkt-group-chevron" (click)="toggleGroupCollapse(grp.name, $event)">
                {{ isGroupCollapsed(grp.name) ? 'chevron_right' : 'expand_more' }}</mat-icon>
              <mat-checkbox class="pkt-group-checkbox" [checked]="areAllSelected(grp.options)"
                [indeterminate]="areSomeSelected(grp.options)" (click)="$event.stopPropagation()"
                (change)="toggleAll(grp.options, $event.checked)">
                {{ grp.name }}
              </mat-checkbox>
              <mat-option *ngFor="let opt of grp.options" [value]="opt.value" [disabled]="opt.disabled"
                [class.pkt-opt-collapsed]="isGroupCollapsed(grp.name)" [matTooltip]="opt.tooltip || ''" [matTooltipClass]="'multiline-tooltip'" matTooltipPosition="above">
                <ng-container *ngIf="optionContent; else defaultGrpLabel1" [ngTemplateOutlet]="optionContent" [ngTemplateOutletContext]="{ $implicit: opt }"></ng-container>
                <ng-template #defaultGrpLabel1>{{ opt.label }}</ng-template>
              </mat-option>
            </mat-optgroup>
            <!-- Collapsible single-select group: chevron + group name header -->
            <mat-optgroup *ngIf="!(multiple && showSelectAll) && collapsibleGroups" class="pkt-optgroup-selectall">
              <mat-icon class="pkt-group-chevron" (click)="toggleGroupCollapse(grp.name, $event)">
                {{ isGroupCollapsed(grp.name) ? 'chevron_right' : 'expand_more' }}</mat-icon>
              <span class="pkt-group-checkbox">{{ grp.name }}</span>
              <mat-option *ngFor="let opt of grp.options" [value]="opt.value" [disabled]="opt.disabled"
                [class.pkt-opt-collapsed]="isGroupCollapsed(grp.name)" [matTooltip]="opt.tooltip || ''" [matTooltipClass]="'multiline-tooltip'" matTooltipPosition="above">
                <ng-container *ngIf="optionContent; else defaultGrpLabel2" [ngTemplateOutlet]="optionContent" [ngTemplateOutletContext]="{ $implicit: opt }"></ng-container>
                <ng-template #defaultGrpLabel2>{{ opt.label }}</ng-template>
              </mat-option>
            </mat-optgroup>
            <!-- Plain static group -->
            <mat-optgroup *ngIf="!(multiple && showSelectAll) && !collapsibleGroups" [label]="grp.name">
              <mat-option *ngFor="let opt of grp.options" [value]="opt.value" [disabled]="opt.disabled" [matTooltip]="opt.tooltip || ''" [matTooltipClass]="'multiline-tooltip'" matTooltipPosition="above">
                <ng-container *ngIf="optionContent; else defaultGrpLabel3" [ngTemplateOutlet]="optionContent" [ngTemplateOutletContext]="{ $implicit: opt }"></ng-container>
                <ng-template #defaultGrpLabel3>{{ opt.label }}</ng-template>
              </mat-option>
            </mat-optgroup>
          </ng-container>
          <ng-container *ngIf="!grp.name">
            <mat-option *ngFor="let opt of grp.options" [value]="opt.value" [disabled]="opt.disabled" [matTooltip]="opt.tooltip || ''" [matTooltipClass]="'multiline-tooltip'" matTooltipPosition="above">
              <ng-container *ngIf="optionContent; else defaultGrpLabel4" [ngTemplateOutlet]="optionContent" [ngTemplateOutletContext]="{ $implicit: opt }"></ng-container>
              <ng-template #defaultGrpLabel4>{{ opt.label }}</ng-template>
            </mat-option>
          </ng-container>
        </ng-container>
      </ng-container>
    </ng-container>
    <mat-option *ngIf="loading" [disabled]="true">
      <div class="pkt-loading-opt"><mat-spinner diameter="16"></mat-spinner> Loading…</div>
    </mat-option>
    <mat-option *ngIf="!loading && showCreateRow" class="pkt-create-option" (click)="onCreate()">
      <span class="pkt-create-prefix">{{ createLabelPrefix }}</span> {{ searchQuery.trim() }}
    </mat-option>
    <div *ngIf="confirmFooter" class="pkt-select-confirm-footer" (click)="$event.stopPropagation()">
      <kt-button-v1 variant="stroked" theme="muted" size="sm" [label]="cancelLabel"
        (btnClick)="cancelSelection()"></kt-button-v1>
      <kt-button-v1 variant="flat" theme="dark" size="sm" [label]="confirmLabel"
        (btnClick)="confirmSelection()"></kt-button-v1>
    </div>
  </mat-select>

  <div *ngIf="hint && !errorMessage" class="pkt-hint">{{ hint }}</div>
  <div *ngIf="errorMessage && errorDisplay === 'inline'" class="pkt-error-msg">
    <span class="material-symbols-outlined">error</span>{{ errorMessage }}
  </div>
  <div *ngIf="errorMessage && errorDisplay === 'floating'" class="pkt-error-floating" role="alert">
    <span class="material-symbols-outlined">error</span>{{ errorMessage }}
  </div>
</div>

<!-- ── Virtual scroll custom overlay ── -->
<div *ngIf="virtualScroll" class="pkt-form-field" [class.pkt-error-state]="!!errorMessage"
  [class.pkt-disabled]="disabled" [ngClass]="'pkt-label-' + labelPosition">
  <label *ngIf="label" class="pkt-label" [class.pkt-label--clamp]="labelLines" [style.--pkt-label-lines]="labelLines">
    {{ label }}<span *ngIf="required" class="pkt-required">*</span>
  </label>

  <div #triggerRef class="pkt-vs-trigger" (click)="toggleDropdown()" [class.open]="isOpen" [class.disabled]="disabled"
    cdkOverlayOrigin #origin="cdkOverlayOrigin">

    <span *ngIf="!hasSelection" class="pkt-vs-placeholder">{{ placeholder }}</span>

    <span *ngIf="hasSelection && !multiple" class="pkt-vs-value">{{ selectedLabel }}</span>

    <div *ngIf="hasSelection && multiple" class="pkt-vs-chips">
      <span class="pkt-chip" *ngFor="let item of selectedItems | slice:0:maxChipsVisible">
        {{ item.label }}
        <button type="button" (click)="removeItem(item, $event)">
          <span class="material-symbols-outlined">close</span>
        </button>
      </span>
      <span *ngIf="selectedItems.length > maxChipsVisible" class="pkt-chip-overflow">
        +{{ selectedItems.length - maxChipsVisible }}
      </span>
    </div>

    <div class="pkt-vs-actions">
      <button *ngIf="clearable && hasSelection" type="button" class="pkt-clear-btn" (click)="clearSelection($event)">
        <span class="material-symbols-outlined">close</span>
      </button>
      <span class="material-symbols-outlined pkt-chevron" [class.rotated]="isOpen">keyboard_arrow_down</span>
    </div>
  </div>

  <ng-template cdkConnectedOverlay [cdkConnectedOverlayOrigin]="origin" [cdkConnectedOverlayOpen]="isOpen"
    [cdkConnectedOverlayWidth]="triggerWidth" [cdkConnectedOverlayHasBackdrop]="true"
    cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop" (backdropClick)="closeDropdown()"
    (detach)="closeDropdown()">
    <div class="pkt-vs-panel" [ngClass]="panelClass">
      <div *ngIf="searchable" class="pkt-vs-search">
        <span class="material-symbols-outlined">search</span>
        <input #vsSearchInput type="text" [placeholder]="searchPlaceholder" [(ngModel)]="searchQuery"
          (ngModelChange)="onSearch($event)" />
      </div>

      <div *ngIf="loading" class="pkt-vs-state">
        <mat-spinner diameter="20"></mat-spinner><span>Loading…</span>
      </div>
      <div *ngIf="!loading && filteredOptions.length === 0" class="pkt-vs-state pkt-vs-empty">
        {{ noResultsText }}
      </div>

      <cdk-virtual-scroll-viewport *ngIf="!loading && filteredOptions.length > 0" [itemSize]="itemSize"
        class="pkt-vs-viewport custom-scrollbar" [style.height.px]="viewportHeight"
        (scrolledIndexChange)="onScrollIndexChange($event)">
        <div *cdkVirtualFor="let opt of filteredOptions; templateCacheSize: 0" class="pkt-vs-option"
          [class.selected]="isSelected(opt)" [class.disabled]="opt.disabled" [style.height.px]="itemSize"
          (click)="selectOption(opt)" [matTooltip]="opt.tooltip || ''" [matTooltipClass]="'multiline-tooltip'" matTooltipPosition="above">
          <mat-checkbox *ngIf="multiple" class="pkt-vs-option-checkbox" [checked]="isSelected(opt)"
            [disabled]="opt.disabled" [disableRipple]="true" tabindex="-1"></mat-checkbox>
          <ng-container *ngIf="optionContent; else vsDefaultLabel"
            [ngTemplateOutlet]="optionContent" [ngTemplateOutletContext]="{ $implicit: opt }"></ng-container>
          <ng-template #vsDefaultLabel>
            <span class="pkt-vs-opt-label">{{ opt.label }}</span>
          </ng-template>
          <span *ngIf="!multiple && isSelected(opt)" class="material-symbols-outlined pkt-vs-check">check</span>
        </div>
      </cdk-virtual-scroll-viewport>
    </div>
  </ng-template>

  <div *ngIf="hint && !errorMessage" class="pkt-hint">{{ hint }}</div>
  <div *ngIf="errorMessage && errorDisplay === 'inline'" class="pkt-error-msg">
    <span class="material-symbols-outlined">error</span>{{ errorMessage }}
  </div>
  <div *ngIf="errorMessage && errorDisplay === 'floating'" class="pkt-error-floating" role="alert">
    <span class="material-symbols-outlined">error</span>{{ errorMessage }}
  </div>
</div>